import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {SpreadsheetEditorState} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetEditorState';
import {
    ISpreadsheetRuntimeStateSnapshot,
    SpreadsheetRuntimeState
} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetRuntimeState';
import {
    ISpreadsheetViewStateSnapshot,
    SpreadsheetViewState
} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetViewState';
import {WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface IWorkbookSnapshot
{
    id:WorkbookId;
    runtime:ISpreadsheetRuntimeStateSnapshot;
    view:ISpreadsheetViewStateSnapshot;
}

export class Workbook
{
    public id:WorkbookId;

    // State required for published app & sync'd across cortex
    @observable
    public runtime:SpreadsheetRuntimeState;

    // State not needed for published app, but sync'd across cortex
    @observable
    public view:SpreadsheetViewState;

    // Transient per-browser-tab state, not serialized into/from DesignState, not sync'd across cortex
    public editor:SpreadsheetEditorState = new SpreadsheetEditorState();

    private constructor()
    {
    }

    public static create():Workbook
    {
        const workbook:Workbook = new Workbook();
        workbook.id = IdUtil.newGUID();

        workbook.runtime = SpreadsheetRuntimeState.create(workbook);
        workbook.view = SpreadsheetViewState.create(workbook.runtime.sheetsMap);

        return workbook;
    }

    public static toJson:ITransformer<Workbook, IWorkbookSnapshot> = createTransformer((workbook:Workbook) =>
    {
        return {
            id: workbook.id,
            runtime: SpreadsheetRuntimeState.toJson(workbook.runtime),
            view: SpreadsheetViewState.toJson(workbook.view)
        };
    });

    public static fromJson(json:IWorkbookSnapshot,
                           currentState?:Workbook,
                           currentJson?:IWorkbookSnapshot,
                           ctx?:DesignStateFromJsonCtx):Workbook
    {
        if( !currentState )
            return Workbook.fromJsonDeep(json);

        if( !currentJson )
            currentJson = Workbook.toJson(currentState);

        const workbook:Workbook = new Workbook();

        workbook.id = json.id;

        workbook.runtime = json.runtime === currentJson.runtime
            ? currentState.runtime
            : SpreadsheetRuntimeState.fromJson(json.runtime, workbook, currentState.runtime, currentJson.runtime, ctx);

        workbook.view = json.view === currentJson.view
            ? currentState.view
            : SpreadsheetViewState.fromJson(json.view, workbook.runtime.sheetsMap, currentState.view, currentJson.view, ctx);

        Workbook._restoreUiState(workbook, currentState);

        return workbook;
    }

    public static fromJsonDeep(json:IWorkbookSnapshot):Workbook
    {
        const workbook:Workbook = new Workbook();

        workbook.id = json.id;
        workbook.runtime = SpreadsheetRuntimeState.fromJsonDeep(json.runtime, workbook);
        workbook.view = SpreadsheetViewState.fromJsonDeep(json.view, workbook.runtime.sheetsMap);

        return workbook;
    }

    private static _restoreUiState(state:Workbook, currentState:Workbook):void
    {
        state.editor = currentState.editor;
    }
}
